// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import breakpoints from "@adl/XDL/PreStyle/breakpoints";
import { Cart } from "@insite/client-framework/Services/CartService";
import { FulfillmentMethod } from "@insite/client-framework/Services/SessionService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import {
    canCheckoutWithCart,
    hasOnlyQuoteRequiredProducts,
    isCartCheckoutDisabled,
} from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getShipToState } from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import translate from "@insite/client-framework/Translate";
import uniq from "lodash/uniq";
import without from "lodash/without";

export const isCheckoutDisabledHandler = (cart: Cart | undefined) => {
    return !canCheckoutWithCart(cart) || hasOnlyQuoteRequiredProducts(cart) || isCartCheckoutDisabled(cart);
};

export function isValidDate(blackOutDays: string[], currentDate: Date) {
    const todayDate = new Date();
    return (
        currentDate.getDay() !== 0 &&
        currentDate.getDay() !== 6 &&
        !blackOutDays.find((item: string) => new Date(item).toDateString() === currentDate.toDateString()) &&
        currentDate.getTime() > todayDate.getTime()
    );
}

/**
 *
 * @param date your date input
 * @returns YYYY-MM-DD formatted date
 */
export const getFormattedDate = (date: Date) => {
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const day = date.getDate().toString().padStart(2, "0");

    return `${year}-${month}-${day}`;
};

function pickMultiple<T extends {}, Exclude extends keyof T>(object: T, keys: Readonly<Exclude[]>): Pick<T, Exclude> {
    const result: any = { ...object };
    Object.keys(object).forEach((key: any) => {
        if (!keys.includes(key)) {
            delete result[key];
        }
    });

    return result;
}

// To load JS script with callback
// example:
// getScripts.urls([ // url1, url2]).then(() => {
// callback handler
// });
export const getScripts = {
    url: (url: string) => {
        return new Promise((resolve, reject) => {
            if (typeof document !== "undefined") {
                const script = document.createElement("script");
                script.type = "text/javascript";
                script.src = url;
                script.addEventListener("load", () => resolve(script), false);
                script.addEventListener("error", () => reject(script), false);
                document.body.appendChild(script);
            }
        });
    },
    urls: (urls: string[]) => {
        return Promise.all(urls.map(getScripts.url));
    },
};

export const addClassName = (classNames = "", value: string) =>
    uniq([...classNames.trim().split(" "), value]).join(" ");

export const removeClassName = (classNames = "", value: string) =>
    without(uniq(classNames.trim().split(" ")), value).join(" ");

export const xIsAuthenticated = (state: ApplicationState): boolean => {
    const session = getSession(state);
    return session && (session.isAuthenticated || session.rememberMe) && !session.isGuest;
};

export const isSmallMobileScreen = () =>
    typeof window !== "undefined" && window.matchMedia("(max-width: 480px)").matches;

export const isMobileScreen = () => typeof window !== "undefined" && window.matchMedia("(max-width: 768px)").matches;

export const isTabletScreen = () => typeof window !== "undefined" && window.matchMedia("(max-width: 1024px)").matches;

export const isWideScreen = () => typeof window !== "undefined" && window.matchMedia("(min-width: 2200px)").matches;

export const isEventSupported = (eventName: string) => {
    let el: any = document.createElement("div");
    eventName = `on${eventName}`;
    let isSupported = eventName in el;
    if (!isSupported) {
        el.setAttribute(eventName, "return;");
        isSupported = typeof el[eventName] === "function";
    }
    el = null;
    return isSupported;
};

export const parseBoolean = (value?: string) => (value ? value?.toLowerCase() === "true" : false);

export const doNothing = () => {};

export const xGetFulfillmentLabel = (state: ApplicationState) => {
    const { fulfillmentMethod, isAuthenticated, rememberMe, isGuest, shipToId, pickUpWarehouse } = getSession(state);
    const shipToState = getShipToState(state, shipToId);
    const { value: shipTo } = shipToState;

    let fulfillmentMethodLabel = "";
    let fulfillmentAddressLabel = "";
    if (fulfillmentMethod === FulfillmentMethod.Ship && (isAuthenticated || rememberMe) && !isGuest && shipTo) {
        fulfillmentMethodLabel = translate("Ship to");
        fulfillmentAddressLabel = shipTo.fullAddress || "";
    } else if (fulfillmentMethod === FulfillmentMethod.PickUp) {
        fulfillmentMethodLabel = translate("Pick up");
        fulfillmentAddressLabel = pickUpWarehouse?.description || pickUpWarehouse?.name || "";
    }
    const shipToAddress = shipTo
        ? `${shipTo?.address1}<br />${shipTo?.city}, ${shipTo?.state?.abbreviation} ${shipTo?.postalCode}`
        : "";
    return { fulfillmentMethodLabel, fulfillmentAddressLabel, fulfillmentMethod, shipTo, pickUpWarehouse };
};

export const stringToKeyGenerator = (str: string) => (str ? str.replace(/ +/g, "") : "");

export const generateSlidesPerViewMap = (opts?: { [key: number]: number }) => {
    const { maxWidths } = breakpoints;
    const values: any = {};
    if (!opts) {
        values[maxWidths[0]] = {
            slidesPerView: 1,
        };
        values[maxWidths[1]] = {
            slidesPerView: 3,
        };
        values[maxWidths[2]] = {
            slidesPerView: 3,
        };
        values[maxWidths[3]] = {
            slidesPerView: 4,
        };
        return values;
    }

    Object.keys(opts).forEach(key => {
        if (!maxWidths[+key]) {
            return;
        }
        values[maxWidths[+key]] = {
            slidesPerView: +opts[+key],
        };
    });
    return values;
};

export const getYoutubeThumbnail = (url: string) => {
    // This regex matches all youtube urls ( embed, shortlinks, full-url )
    if (!url) {
        return "";
    }
    const match = url.match(
        /^((?:https?:)?\/\/)?((?:www|m)\.)?((?:youtube(-nocookie)?\.com|youtu.be))(\/(?:[\w\-]+\?v=|shorts\/|embed\/|v\/)?)([\w\-]+)(\S+)?$/,
    );
    if (!match?.length) {
        return "";
    }
    const videoId = match[6];
    return `https://img.youtube.com/vi/${videoId}/default.jpg`;
};

const camelCase = (str: string) => {
    return str
        .replace(/\s(.)/g, $1 => {
            return $1.toUpperCase();
        })
        .replace(/\s/g, "")
        .replace(/^(.)/, $1 => {
            return $1.toLowerCase();
        });
};
export const camelCaseKeys = (obj: any) => {
    return Object.keys(obj).reduce((accumulator: any, key: string) => {
        accumulator[camelCase(key)] = obj[key];
        return accumulator;
    }, {});
};
export const isOptional = (isOptional: boolean) => {
    return isOptional ? translate(" (Optional)") : "";
};

/**
 *
 * we have used this equation vw=(fontSize/viewportwidth) × 100 to adjust font size on tablet screen
 * @param mobile
 * @param tablet
 * @param desktop
 * @returns
 */
export const autoClamp = (mobile: number, tablet: number, desktop: number) => {
    const vw = ((tablet / breakpoints.values[2]) * 100).toFixed(2);
    return `clamp(${mobile}px ,${vw}vw , ${desktop}px)`;
};
